import { useState, useMemo } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  Loader2,
  RotateCcw,
  Sparkles,
  Mail,
  Calendar,
  Download,
  Lock,
} from "lucide-react";
import { toast } from "sonner";
import { QUESTIONS, DIMENSIONS } from "@/data/questions";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const CALENDLY_URL = process.env.REACT_APP_CALENDLY_URL;

const STAGE = {
  INTRO: "intro",
  QUIZ: "quiz",
  GATE: "gate",
  LOADING: "loading",
  REPORT: "report",
};

// Mirror of backend compute_raw_dimension_scores for client-side preview only.
// Context-only questions (dimension not in DIMENSIONS) are excluded from scoring.
function computePreviewScores(answersByQid) {
  const byDim = {};
  for (const q of QUESTIONS) {
    if (!DIMENSIONS.includes(q.dimension)) continue;
    const a = answersByQid[q.id];
    if (!a) continue;
    (byDim[q.dimension] ||= []).push(a.value);
  }
  const dims = {};
  for (const [dim, vals] of Object.entries(byDim)) {
    const avg = vals.reduce((s, v) => s + v, 0) / vals.length;
    dims[dim] = Math.round(((avg - 1) / 3) * 100);
  }
  const list = Object.values(dims);
  const overall = list.length
    ? Math.round(list.reduce((s, v) => s + v, 0) / list.length)
    : 0;
  let tier = "Foundational";
  if (overall >= 85) tier = "Transformative";
  else if (overall >= 65) tier = "Advanced";
  else if (overall >= 45) tier = "Operational";
  else if (overall >= 25) tier = "Emerging";
  return { overall, tier, dims };
}

export default function AuditWizard() {
  const [stage, setStage] = useState(STAGE.INTRO);
  const [lead, setLead] = useState({
    name: "",
    email: "",
    company: "",
    role: "",
    revenue_range: "",
  });
  const [answers, setAnswers] = useState({});
  const [stepIdx, setStepIdx] = useState(0);
  const [report, setReport] = useState(null);

  const totalQuestions = QUESTIONS.length;
  const currentQ = QUESTIONS[stepIdx];

  const progress = useMemo(() => {
    if (stage === STAGE.QUIZ) return ((stepIdx + 1) / totalQuestions) * 100;
    if (stage === STAGE.GATE) return 100;
    if (stage === STAGE.LOADING || stage === STAGE.REPORT) return 100;
    return 0;
  }, [stage, stepIdx, totalQuestions]);

  const preview = useMemo(
    () => (stage === STAGE.GATE ? computePreviewScores(answers) : null),
    [stage, answers]
  );

  const beginAudit = () => {
    setStage(STAGE.QUIZ);
    setStepIdx(0);
  };

  const choose = (option) => {
    setAnswers((prev) => ({
      ...prev,
      [currentQ.id]: {
        label: option.label,
        value: option.value,
        dimension: currentQ.dimension,
      },
    }));
  };

  const next = () => {
    if (!answers[currentQ.id]) {
      toast.error("Pick one option to continue.");
      return;
    }
    if (stepIdx < totalQuestions - 1) {
      setStepIdx((i) => i + 1);
    } else {
      // All answered → preview + lead gate
      setStage(STAGE.GATE);
    }
  };

  const prev = () => {
    if (stepIdx > 0) setStepIdx((i) => i - 1);
  };

  const submitAudit = async (e) => {
    e?.preventDefault();
    if (!lead.name || !lead.email || !lead.company) {
      toast.error("Please complete name, email and company to unlock your full audit.");
      return;
    }
    setStage(STAGE.LOADING);
    try {
      const payload = {
        ...lead,
        answers: QUESTIONS.map((q) => ({
          question_id: q.id,
          dimension: q.dimension,
          value: answers[q.id].value,
          label: answers[q.id].label,
        })),
      };
      const res = await axios.post(`${API}/audit/submit`, payload, {
        timeout: 90000,
      });
      setReport(res.data);
      setStage(STAGE.REPORT);
    } catch (err) {
      console.error(err);
      toast.error("We couldn't generate your scorecard. Please try again.");
      setStage(STAGE.GATE);
    }
  };

  const reset = () => {
    setStage(STAGE.INTRO);
    setLead({ name: "", email: "", company: "", role: "", revenue_range: "" });
    setAnswers({});
    setStepIdx(0);
    setReport(null);
  };

  return (
    <section
      id="audit"
      data-testid="audit-section"
      className="relative py-24 md:py-32 border-y border-white/10 bg-[#070708]"
    >
      <div className="mx-auto max-w-7xl px-6 md:px-12 lg:px-16">
        <div className="flex flex-col items-start gap-4 mb-12">
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-[#00E5FF]">
            / The Diagnostic
          </div>
          <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tighter max-w-3xl">
            Your AI Audit Scorecard
          </h2>
        </div>

        <div
          data-testid="wizard-container"
          className="border border-white/10 bg-[#050505] relative overflow-hidden"
        >
          {/* Progress bar */}
          <div className="h-[2px] bg-white/5">
            <motion.div
              data-testid="wizard-progress"
              className="h-full bg-[#00E5FF]"
              animate={{ width: `${progress}%` }}
              transition={{ type: "spring", stiffness: 90, damping: 18 }}
            />
          </div>

          <div className="p-6 md:p-12 min-h-[560px] flex flex-col">
            <AnimatePresence mode="wait">
              {stage === STAGE.INTRO && <IntroStage onBegin={beginAudit} key="intro" />}

              {stage === STAGE.QUIZ && (
                <QuizStage
                  key={`q-${currentQ.id}`}
                  stepIdx={stepIdx}
                  totalQuestions={totalQuestions}
                  question={currentQ}
                  selectedValue={answers[currentQ.id]?.value}
                  onChoose={choose}
                  onPrev={prev}
                  onNext={next}
                />
              )}

              {stage === STAGE.GATE && preview && (
                <GateStage
                  key="gate"
                  preview={preview}
                  lead={lead}
                  setLead={setLead}
                  onSubmit={submitAudit}
                />
              )}

              {stage === STAGE.LOADING && <LoadingStage key="loading" />}

              {stage === STAGE.REPORT && report && (
                <Report key="report" report={report} onReset={reset} />
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ====================== STAGES ====================== */

function IntroStage({ onBegin }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.35 }}
      className="flex-1 grid grid-cols-1 md:grid-cols-5 gap-10 md:gap-12 items-center"
    >
      <div className="md:col-span-3">
        <div className="font-mono text-xs uppercase tracking-widest text-white/50">
          Step 00, Ready when you are
        </div>
        <h3 className="font-heading text-3xl md:text-4xl font-semibold mt-4 tracking-tight">
          15 questions. 5 dimensions. One scorecard built for you.
        </h3>
        <p className="text-white/60 mt-5 leading-relaxed">
          We&apos;ll ask about strategy, data, automation, team and commercial
          outcomes. You see your preliminary score the moment you finish, then
          unlock the full report and PDF with one email.
        </p>
        <ul className="mt-8 space-y-3">
          {[
            "5-minute focused diagnostic, no fluff",
            "Personalised report generated against your exact answers",
            "PDF delivered to your inbox + Calendly follow-up available",
          ].map((t) => (
            <li key={t} className="flex items-start gap-3 text-sm text-white/75">
              <CheckCircle2 className="h-4 w-4 text-[#00E5FF] mt-0.5 shrink-0" />
              {t}
            </li>
          ))}
        </ul>
        <div className="mt-10">
          <button
            data-testid="wizard-begin-btn"
            onClick={onBegin}
            className="group inline-flex items-center gap-2 bg-[#00E5FF] text-black font-medium hover:bg-[#00B8CC] transition-colors px-8 py-4 uppercase tracking-widest text-sm"
          >
            Get my scorecard
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </button>
        </div>
      </div>

      {/* Sample scorecard preview tile */}
      <div className="md:col-span-2">
        <div
          data-testid="wizard-preview-tile"
          className="border border-white/12 bg-gradient-to-br from-[#0a0a0c] to-[#050505] p-6 md:p-7 relative"
        >
          <div className="flex items-center justify-between">
            <div className="font-mono text-[10px] uppercase tracking-widest text-white/45">
              Sample scorecard
            </div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#00E5FF]">
              Operational
            </div>
          </div>
          <div className="mt-5 flex items-baseline gap-2">
            <span className="font-heading text-6xl font-bold text-[#00E5FF] glow-text leading-none">
              52
            </span>
            <span className="text-white/40 font-mono text-sm">/100</span>
          </div>
          <div className="mt-2 text-xs text-white/55">Overall AI maturity</div>

          <div className="mt-6 space-y-3">
            {DIMENSIONS.map((d, i) => {
              const fake = [70, 55, 48, 40, 47][i] ?? 50;
              return (
                <div key={d}>
                  <div className="flex items-baseline justify-between text-[11px]">
                    <span className="text-white/70">{d}</span>
                    <span className="font-mono text-[#00E5FF]">{fake}</span>
                  </div>
                  <div className="mt-1 h-[2px] bg-white/10 overflow-hidden">
                    <div
                      className="h-full bg-[#00E5FF]/80"
                      style={{ width: `${fake}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-6 pt-5 border-t border-white/10 flex items-center justify-between">
            <div className="font-mono text-[10px] uppercase tracking-widest text-white/40">
              Yours unlocks at the end
            </div>
            <Lock className="h-3.5 w-3.5 text-white/40" />
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function QuizStage({ stepIdx, totalQuestions, question, selectedValue, onChoose, onPrev, onNext }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.3 }}
      className="flex-1 flex flex-col justify-center max-w-3xl"
    >
      <div className="flex items-center justify-between font-mono text-xs uppercase tracking-widest text-white/50">
        <span data-testid="quiz-step-indicator">
          {String(stepIdx + 1).padStart(2, "0")} / {String(totalQuestions).padStart(2, "0")}
        </span>
        <span>{question.dimension}</span>
      </div>
      <h3
        data-testid="quiz-question"
        className="font-heading text-3xl md:text-4xl font-semibold mt-6 tracking-tight"
      >
        {question.question}
      </h3>

      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-3">
        {question.options.map((opt, idx) => {
          const selected = selectedValue === opt.value;
          return (
            <button
              key={opt.value}
              data-testid={`quiz-option-${question.id}-${opt.value}`}
              onClick={() => onChoose(opt)}
              className={`group text-left p-5 border transition-all flex items-start gap-4 ${
                selected
                  ? "border-[#00E5FF] bg-[#00E5FF]/10"
                  : "border-white/12 hover:border-white/40 bg-white/[0.02]"
              }`}
            >
              <div
                className={`mt-0.5 flex h-7 w-7 items-center justify-center border text-xs font-mono ${
                  selected ? "border-[#00E5FF] text-[#00E5FF]" : "border-white/30 text-white/50"
                }`}
              >
                {String.fromCharCode(65 + idx)}
              </div>
              <div className="flex-1">
                <div className="text-white text-base md:text-lg leading-snug">{opt.label}</div>
              </div>
              {selected && <CheckCircle2 className="h-5 w-5 text-[#00E5FF] mt-0.5" />}
            </button>
          );
        })}
      </div>

      <div className="mt-10 flex items-center justify-between">
        <button
          data-testid="quiz-prev-btn"
          onClick={onPrev}
          disabled={stepIdx === 0}
          className="inline-flex items-center gap-2 border border-white/15 text-white/70 hover:bg-white/5 hover:text-white px-6 py-3 uppercase tracking-widest text-sm disabled:opacity-30 disabled:hover:bg-transparent"
        >
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        <button
          data-testid="quiz-next-btn"
          onClick={onNext}
          className="group inline-flex items-center gap-2 bg-[#00E5FF] text-black font-medium hover:bg-[#00B8CC] transition-colors px-8 py-3 uppercase tracking-widest text-sm"
        >
          {stepIdx === totalQuestions - 1 ? "See my preliminary score" : "Next"}
          <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
        </button>
      </div>
    </motion.div>
  );
}

function GateStage({ preview, lead, setLead, onSubmit }) {
  return (
    <motion.form
      onSubmit={onSubmit}
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.35 }}
      data-testid="gate-form"
      className="flex-1 grid grid-cols-1 md:grid-cols-5 gap-10 md:gap-12"
    >
      {/* Preview score */}
      <div className="md:col-span-2">
        <div className="font-mono text-xs uppercase tracking-widest text-[#00E5FF]">
          Preliminary score
        </div>
        <div className="mt-4 flex items-baseline gap-3">
          <span
            data-testid="gate-preview-score"
            className="font-heading text-7xl md:text-8xl font-bold text-[#00E5FF] glow-text leading-none"
          >
            {preview.overall}
          </span>
          <span className="text-white/40 font-mono">/100</span>
        </div>
        <div className="mt-3 font-mono text-xs uppercase tracking-widest text-white/70">
          Tier · {preview.tier}
        </div>

        <div className="mt-8 space-y-4">
          {DIMENSIONS.map((d) => {
            const score = preview.dims[d] ?? 0;
            return (
              <div key={d}>
                <div className="flex items-baseline justify-between text-xs">
                  <span className="text-white/75">{d}</span>
                  <span className="font-mono text-[#00E5FF]">{score}</span>
                </div>
                <div className="mt-1.5 h-[3px] bg-white/10 overflow-hidden">
                  <motion.div
                    className="h-full bg-[#00E5FF]"
                    initial={{ width: 0 }}
                    animate={{ width: `${score}%` }}
                    transition={{ duration: 0.7, ease: "easeOut" }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Lead gate */}
      <div className="md:col-span-3 md:border-l md:border-white/10 md:pl-10">
        <div className="font-mono text-xs uppercase tracking-widest text-white/50">
          Unlock the full audit
        </div>
        <h3 className="font-heading text-3xl md:text-4xl font-semibold mt-4 tracking-tight">
          Where should we send your full report?
        </h3>
        <p className="text-white/55 mt-4 text-sm leading-relaxed max-w-md">
          We&apos;ll generate the in-depth AI-written analysis, recommendations and PDF,
          delivered to your inbox in under 60 seconds.
        </p>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
          <LeadInput
            testid="lead-name"
            label="Full name *"
            value={lead.name}
            onChange={(v) => setLead({ ...lead, name: v })}
            placeholder="Jane Cooper"
          />
          <LeadInput
            testid="lead-email"
            label="Work email *"
            type="email"
            value={lead.email}
            onChange={(v) => setLead({ ...lead, email: v })}
            placeholder="jane@company.com"
          />
          <LeadInput
            testid="lead-company"
            label="Company *"
            value={lead.company}
            onChange={(v) => setLead({ ...lead, company: v })}
            placeholder="Acme Construction"
          />
          <LeadInput
            testid="lead-role"
            label="Your role"
            value={lead.role}
            onChange={(v) => setLead({ ...lead, role: v })}
            placeholder="MD, CRO, Head of Sales…"
          />
          <div className="md:col-span-2">
            <label className="block text-xs font-mono uppercase tracking-widest text-white/50 mb-2">
              Annual revenue range
            </label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {["<£1M", "£1M–£10M", "£10M–£50M", "£50M+"].map((r) => (
                <button
                  key={r}
                  type="button"
                  data-testid={`lead-revenue-${r}`}
                  onClick={() => setLead({ ...lead, revenue_range: r })}
                  className={`px-4 py-3 border text-sm transition-colors ${
                    lead.revenue_range === r
                      ? "border-[#00E5FF] bg-[#00E5FF]/10 text-white"
                      : "border-white/15 text-white/70 hover:border-white/40"
                  }`}
                >
                  {r}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-10 flex items-center gap-4 flex-wrap">
          <button
            type="submit"
            data-testid="gate-submit-btn"
            className="group inline-flex items-center gap-2 bg-[#00E5FF] text-black font-medium hover:bg-[#00B8CC] transition-colors px-8 py-4 uppercase tracking-widest text-sm"
          >
            Unlock my full audit
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </button>
          <p className="text-xs text-white/40 font-mono uppercase tracking-wider">
            We never share your data.
          </p>
        </div>
      </div>
    </motion.form>
  );
}

function LoadingStage() {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="flex-1 flex flex-col items-center justify-center text-center"
      data-testid="wizard-loading"
    >
      <div className="h-14 w-14 grid place-items-center border border-[#00E5FF]/40 text-[#00E5FF] glow-border">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
      <div className="mt-8 font-heading text-2xl md:text-3xl font-semibold tracking-tight">
        Generating your AI Audit Scorecard…
      </div>
      <p className="mt-3 text-white/55 max-w-md">
        Crunching your answers across 5 dimensions and writing your personalised
        commercial action plan.
      </p>
    </motion.div>
  );
}

function LeadInput({ label, value, onChange, placeholder, type = "text", testid }) {
  return (
    <label className="block">
      <span className="block text-xs font-mono uppercase tracking-widest text-white/50 mb-2">
        {label}
      </span>
      <input
        data-testid={testid}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-white/5 border border-white/15 text-white px-4 py-3 outline-none focus:border-[#00E5FF] focus:ring-1 focus:ring-[#00E5FF] transition-colors placeholder-white/30"
      />
    </label>
  );
}

function Report({ report, onReset }) {
  const downloadPdf = () => {
    window.open(`${API}/audit/${report.id}/pdf`, "_blank", "noopener");
  };

  return (
    <motion.div
      key="report"
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45 }}
      className="flex-1"
      data-testid="audit-report"
    >
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
        <div>
          <div className="font-mono text-xs uppercase tracking-widest text-[#00E5FF]">
            / Your scorecard, {report.tier}
          </div>
          <h3 className="font-heading text-3xl md:text-4xl font-semibold mt-4 tracking-tight max-w-2xl">
            {report.headline}
          </h3>
          <div
            data-testid="report-email-sent"
            className="mt-4 inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-white/55"
          >
            <Mail className="h-3.5 w-3.5 text-[#00E5FF]" />
            PDF on its way to {report.email}
          </div>
        </div>
        <div className="flex flex-wrap gap-3">
          <button
            data-testid="report-download-btn"
            onClick={downloadPdf}
            className="inline-flex items-center gap-2 border border-white/15 text-white/85 hover:bg-white/5 hover:text-white px-5 py-3 uppercase tracking-widest text-xs"
          >
            <Download className="h-3.5 w-3.5" /> Download PDF
          </button>
          <a
            data-testid="report-book-btn"
            href={CALENDLY_URL}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 bg-[#00E5FF] text-black hover:bg-[#00B8CC] px-5 py-3 uppercase tracking-widest text-xs font-medium"
          >
            <Calendar className="h-3.5 w-3.5" /> Book follow-up
          </a>
          <button
            data-testid="report-reset-btn"
            onClick={onReset}
            className="inline-flex items-center gap-2 border border-white/15 text-white/70 hover:text-white hover:bg-white/5 px-5 py-3 uppercase tracking-widest text-xs"
          >
            <RotateCcw className="h-3.5 w-3.5" /> Retake
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 border border-white/10 mt-10">
        <div className="bg-[#050505] p-8 md:p-10 md:row-span-2 flex flex-col">
          <div className="font-mono text-xs uppercase tracking-widest text-white/50">Overall maturity</div>
          <div className="mt-4 flex items-baseline gap-3">
            <span
              data-testid="report-overall-score"
              className="font-heading text-7xl md:text-8xl font-bold text-[#00E5FF] glow-text leading-none"
            >
              {report.overall_score}
            </span>
            <span className="text-white/40 font-mono">/100</span>
          </div>
          <div className="mt-3 text-sm uppercase tracking-widest text-white/70 font-mono">{report.tier}</div>
          <div className="mt-8 h-px bg-white/10" />
          <p className="mt-6 text-white/70 leading-relaxed text-sm md:text-base flex-1">{report.summary}</p>
        </div>

        <div className="bg-[#050505] p-8 md:p-10 md:col-span-2">
          <div className="font-mono text-xs uppercase tracking-widest text-white/50 mb-6">Dimensions</div>
          <div className="space-y-5">
            {report.dimensions.map((d) => (
              <div key={d.name} data-testid={`report-dim-${d.name}`}>
                <div className="flex items-baseline justify-between">
                  <div className="text-sm md:text-base">{d.name}</div>
                  <div className="font-mono text-sm text-[#00E5FF]">{d.score}</div>
                </div>
                <div className="mt-2 h-[3px] bg-white/8 overflow-hidden">
                  <motion.div
                    className="h-full bg-[#00E5FF]"
                    initial={{ width: 0 }}
                    animate={{ width: `${d.score}%` }}
                    transition={{ duration: 0.8, ease: "easeOut" }}
                  />
                </div>
                {d.insight && <p className="mt-2 text-xs text-white/55 leading-relaxed">{d.insight}</p>}
              </div>
            ))}
          </div>
        </div>

        <div className="bg-[#050505] p-8 md:p-10">
          <div className="font-mono text-xs uppercase tracking-widest text-[#00E5FF] mb-4">Strengths</div>
          <ul className="space-y-3 text-sm text-white/75">
            {report.strengths.map((s, i) => (
              <li key={i} className="flex gap-3">
                <span className="text-[#00E5FF] mt-1">+</span> <span>{s}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="bg-[#050505] p-8 md:p-10">
          <div className="font-mono text-xs uppercase tracking-widest text-white/50 mb-4">Gaps</div>
          <ul className="space-y-3 text-sm text-white/75">
            {report.gaps.map((g, i) => (
              <li key={i} className="flex gap-3">
                <span className="text-white/40 mt-1">·</span> <span>{g}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="mt-px grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10 border border-white/10 border-t-0">
        <div className="bg-[#050505] p-8 md:p-10 md:col-span-2">
          <div className="font-mono text-xs uppercase tracking-widest text-[#00E5FF] mb-6 flex items-center gap-2">
            <Sparkles className="h-3.5 w-3.5" /> Recommended growth moves
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {report.recommendations.map((r, i) => (
              <div key={i} className="border border-white/10 p-5">
                <div className="font-mono text-xs text-white/50">{String(i + 1).padStart(2, "0")}</div>
                <div className="font-heading text-lg font-semibold mt-2">{r.title}</div>
                <p className="text-white/65 mt-2 text-sm leading-relaxed">{r.description}</p>
              </div>
            ))}
          </div>
          {report.next_step && (
            <div className="mt-8 p-6 border border-[#00E5FF]/40 bg-[#00E5FF]/[0.04] flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <div className="font-mono text-xs text-[#00E5FF] uppercase tracking-widest">Next step</div>
                <div className="mt-2 text-white/90 max-w-2xl">{report.next_step}</div>
              </div>
              <a
                data-testid="report-next-step-cta"
                href={CALENDLY_URL}
                target="_blank"
                rel="noreferrer"
                className="shrink-0 inline-flex items-center gap-2 bg-[#00E5FF] text-black hover:bg-[#00B8CC] px-5 py-3 uppercase tracking-widest text-xs font-medium"
              >
                <Calendar className="h-3.5 w-3.5" /> Book the engagement
              </a>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}
